import { Box, Container, Divider, FormLabel } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import ButtonCreateNew from 'components/ui/ButtonCreateNew';
import CheckboxSelect from 'components/ui/CheckboxSelect/CheckboxSelect';
import FilterGroup from 'components/ui/FilterGroup';
import HeaderBar from 'components/ui/HeaderBar';
import SearchInput from 'components/ui/SearchInput';
import ActiveTag from 'components/ui/ActiveTag';
import DateTime from 'components/ui/DateTime';
import UserRoleTag from 'components/ui/UserRole/UserRoleTag';
import { UserRole, UserRoleType } from 'components/ui/UserRole/user-role.enum';
import ServerDynamicTableProvider from 'contexts/server-table/ServerDynamicTableContext';
import useServerTableUrlState from 'hooks/server-table/useServerTableUrlState';
import React from 'react';
import UsersList from '../components/UsersList';

type UserFilterKey = 'roleIn' | 'activeIn';

const userFilterDefaults: Record<UserFilterKey, string> = {
  roleIn: '',
  activeIn: '',
};

export default function PageUsers() {
  const {
    pagination,
    globalFilter,
    filters,
    setPagination,
    setGlobalFilter,
    setFilter,
  } = useServerTableUrlState<UserFilterKey>({
    filterDefaults: userFilterDefaults,
  });

  const columns = React.useMemo<ColumnDef<Record<string, unknown>>[]>(
    () => [
      {
        header: 'Nome',
        accessorKey: 'name',
      },
      {
        header: 'E-mail',
        accessorKey: 'email',
      },
      {
        header: 'Status',
        accessorKey: 'active',
        cell: ({ row }) => <ActiveTag isActive={Boolean(row.original.active)} />,
      },
      {
        header: 'Tipo de perfil',
        accessorKey: 'role',
        cell: ({ row }) => <UserRoleTag role={row.original.role as UserRoleType} />,
      },
      {
        header: 'Cadastrado em',
        accessorKey: 'createdAt',
        cell: ({ row }) => <DateTime date={row.original.createdAt as string} />,
      },
    ],
    [],
  );

  const roleOptions = Object.entries(UserRole).map(([value, label]) => ({
    value,
    label,
  }));
  const statusOptions = [
    { value: 'true', label: 'Ativo' },
    { value: 'false', label: 'Inativo' },
  ];
  const selectedRoleValues = parseCsv(filters.roleIn);

  return (
    <ServerDynamicTableProvider
      columns={columns}
      pagination={pagination}
      globalFilter={globalFilter}
      onPaginationChange={setPagination}
      onGlobalFilterChange={setGlobalFilter}
      resource={{
        resourceName: 'users',
        params: {
          page: pagination.pageIndex + 1,
          pageSize: pagination.pageSize,
          q: globalFilter || undefined,
          roleIn: filters.roleIn || undefined,
          activeIn: filters.activeIn || undefined,
        },
      }}
    >
      <HeaderBar
        title="Usuários"
        description="Visualize a lista de usuários Patri"
      >
        <ButtonCreateNew 
          title="Cadastrar novo"
          to="/usuarios/novo"
        />
      </HeaderBar>
      
      <Divider />
      
      <Container flexDirection="column">
        <FilterGroup justifyContent="space-between" pos="relative">
          <Box flex={1} minW="180px">
            <FormLabel>Buscar</FormLabel>
            <SearchInput
              placeholder="Busque pelo nome"
              value={globalFilter}
              onChange={(value) => setGlobalFilter(String(value))}
            />
          </Box>
          <Box>
            <FormLabel>Filtrar por tipo de perfil</FormLabel>
            <CheckboxSelect
              placeholder="Todos os perfis"
              options={roleOptions}
              value={roleOptions.filter((option) =>
                selectedRoleValues.includes(option.value),
              )}
              onChange={(value) =>
                setFilter(
                  'roleIn',
                  value.map((item) => String(item.value)).join(','),
                )
              }
            />
          </Box>
          <Box>
            <FormLabel>Filtrar por status</FormLabel>
            <CheckboxSelect
              placeholder="Todos os status"
              options={statusOptions}
              value={statusOptions.filter((option) =>
                parseCsv(filters.activeIn).includes(option.value),
              )}
              onChange={(value) =>
                setFilter(
                  'activeIn',
                  value.map((item) => String(item.value)).join(','),
                )
              }
            />
          </Box>
        </FilterGroup>

        <UsersList />
      </Container>
    </ServerDynamicTableProvider>
  )
}

const parseCsv = (value?: string) => {
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};
